import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Phone, PhoneOff, PhoneMissed, Video } from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getInitials } from '@/lib/utils';
import { useAuthStore, useChatStore } from '@/stores';
import {
  isCallMediaType,
  isVideoCallMediaType,
} from '@/services/calls/types';
import { callEngine } from '@/lib/calls/call-engine';
import type { RawChatMessage } from '@/services/chats/types';

export const Route = createFileRoute('/(app)/productivity/chats/calls')({
  component: CallsHistoryRoute,
});

type CallFilter = 'all' | 'missed';

interface CallRow {
  message: RawChatMessage;
  peerId: number;
  peerName: string;
  peerImage: string;
  isVideo: boolean;
  isOutgoing: boolean;
  isMissed: boolean;
  isCurrentUser: boolean;
}

/** Best-effort missed-call detection from the call-invite payload. */
function isMissed(message: RawChatMessage): boolean {
  const mo = (message.media_object ?? null) as Record<string, unknown> | null;
  const callData = (mo?.call_data ?? mo) as Record<string, unknown> | undefined;
  const state = String(callData?.call_state ?? '').toUpperCase();
  if (
    state === 'MISSED' ||
    state === 'NOT_ANSWERED' ||
    state === 'REJECTED' ||
    state === 'BUSY'
  ) {
    return true;
  }
  // Server sometimes stamps `chat_text` with the human label.
  const txt = (message.chat_text || '').toLowerCase();
  return (
    txt.includes('missed') ||
    txt.includes('no answer') ||
    txt.includes('not answered')
  );
}

function CallsHistoryRoute() {
  const currentUser = useAuthStore((s) => s.user);
  const currentUserId = currentUser?.id ?? currentUser?.user_id ?? 0;
  const threads = useChatStore((s) => s.threads);

  const [filter, setFilter] = useState<CallFilter>('all');

  // Build a flat, time-ordered list of call rows across every thread the
  // chat store knows about. Group chats are skipped — there's no clear peer
  // to call back on a group history row, and group calls aren't supported
  // yet (see the SOD note).
  const rows = useMemo<CallRow[]>(() => {
    const out: CallRow[] = [];
    for (const thread of threads) {
      const isGroup = thread.meta.chat_mode === 'group';
      if (isGroup) continue;
      const peerId = thread.meta.userId;
      const peerName = thread.meta.title;
      const peerImage = thread.meta.avatar;
      if (!peerId) continue;
      for (const m of thread.messages) {
        if (!isCallMediaType(m.media_type)) continue;
        const isOutgoing = m.from_user_id === currentUserId;
        out.push({
          message: m,
          peerId,
          peerName,
          peerImage,
          isVideo: isVideoCallMediaType(m.media_type),
          isOutgoing,
          isMissed: isMissed(m) && !isOutgoing,
          isCurrentUser: isOutgoing,
        });
      }
    }
    // Newest first.
    return out.sort((a, b) => b.message.created_time - a.message.created_time);
  }, [threads, currentUserId]);

  const visible = filter === 'missed' ? rows.filter((r) => r.isMissed) : rows;

  const handleCallBack = (row: CallRow) => {
    callEngine.startCall({
      peerId: row.peerId,
      peerName: row.peerName,
      peerImage: row.peerImage,
      media: row.isVideo ? 'video' : 'audio',
    });
  };

  return (
    <Card className="h-[85vh] flex flex-col">
      <CardHeader className="flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle>Call history</CardTitle>
        <Tabs
          value={filter}
          onValueChange={(v) => setFilter(v as CallFilter)}
          className="w-auto"
        >
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="missed">Missed</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col p-0">
        <div className="flex-1 overflow-y-auto divide-y">
          {visible.length === 0 ? (
            <div className="flex h-full items-center justify-center px-6 py-12 text-sm text-muted-foreground">
              {filter === 'missed' ? 'No missed calls.' : 'No call history yet.'}
            </div>
          ) : (
            visible.map((row) => (
              <CallHistoryRow
                key={`${row.peerId}-${row.message.id}-${row.message.unique_message_id}`}
                row={row}
                onCallBack={handleCallBack}
              />
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface CallHistoryRowProps {
  row: CallRow;
  onCallBack: (row: CallRow) => void;
}

function CallHistoryRow({ row, onCallBack }: CallHistoryRowProps) {
  const { peerName, peerImage, isOutgoing, isMissed, isVideo, message } = row;

  const Icon = isMissed
    ? PhoneMissed
    : isVideo
      ? Video
      : isOutgoing
        ? PhoneOff
        : Phone;
  const iconCls = isMissed
    ? 'text-red-600 dark:text-red-400'
    : isOutgoing
      ? 'text-muted-foreground'
      : 'text-emerald-600 dark:text-emerald-400';
  const directionLabel = isMissed
    ? 'Missed call'
    : isOutgoing
      ? `Outgoing ${isVideo ? 'video' : 'voice'} call`
      : `Incoming ${isVideo ? 'video' : 'voice'} call`;

  const time = (() => {
    const ms = message.created_time;
    if (!ms) return '';
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '';
    if (isToday(d)) return format(d, 'HH:mm');
    if (isYesterday(d)) return `Yesterday ${format(d, 'HH:mm')}`;
    return format(d, 'dd MMM, HH:mm');
  })();

  return (
    <div className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/40">
      <Avatar className="size-10 shrink-0">
        <AvatarImage src={peerImage || undefined} alt={peerName} />
        <AvatarFallback className="bg-muted-foreground/20 text-foreground text-xs font-medium">
          {getInitials(peerName || 'User')}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p
          className={`truncate text-sm font-medium ${
            isMissed ? 'text-red-600 dark:text-red-400' : ''
          }`}
        >
          {peerName || 'Unknown'}
        </p>
        <p className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
          <Icon className={`size-3.5 shrink-0 ${iconCls}`} />
          <span>{directionLabel}</span>
          {time && <span>· {time}</span>}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Call back"
          onClick={() => onCallBack(row)}
        >
          <Phone className="size-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Video call"
          onClick={() =>
            onCallBack({
              ...row,
              isVideo: true,
            })
          }
        >
          <Video className="size-4" />
        </Button>
      </div>
    </div>
  );
}
